/**
 * API Client Configuration
 * Creates instances of Auth and ERP service clients with proper authentication
 *
 * ERP client supports dynamic base URLs via ERPConfigContext for multi-tenant deployments
 */

import createERPService, { type ERPServiceClient } from '@kisanlink/erp-service';
import createAAAService from 'auth-service';
import { config } from '@/config/env';
import { store } from '@/store';

/**
 * Get the current access token from Redux store
 */
const getAccessToken = (): string | undefined => {
  const state = store.getState();
  const token = state.auth.accessToken || undefined;

  // Debug logging in development
  if (import.meta.env.DEV) {
    if (!token) {
      console.warn(
        '[API Client] No access token found in Redux store. User may not be authenticated.'
      );
      console.log('[API Client] Auth state:', {
        isAuthenticated: state.auth.isAuthenticated,
        hasToken: !!token,
        user: state.auth.user?.id || 'none',
      });
    }
  }

  return token;
};

/**
 * Get the current organization ID from localStorage (set by OrganizationContext)
 * or from the user's context in Redux store.
 * This is used to add the X-Organization-Id header for multi-tenant security.
 */
const getOrganizationId = (): string | undefined => {
  // First, try localStorage (set by OrganizationContext when user selects an org)
  const storedOrgId = localStorage.getItem('organizationId');
  if (storedOrgId) {
    return storedOrgId;
  }

  // Fallback to extracting from user's JWT claims
  const state = store.getState();
  const user = state.auth.user;

  // Extract organization ID from user context
  // The user object may contain organization info from the JWT
  if (user && 'organization_id' in user) {
    return (user as any).organization_id;
  }

  // Try to get from organizations array if available
  if (user && 'organizations' in user) {
    const orgs = (user as any).organizations;
    if (Array.isArray(orgs) && orgs.length > 0) {
      return orgs[0].id || orgs[0].organization_id;
    }
  }

  // Try organization_ids array
  if (user && 'organization_ids' in user) {
    const orgIds = (user as any).organization_ids;
    if (Array.isArray(orgIds) && orgIds.length > 0) {
      return orgIds[0];
    }
  }

  return undefined;
};

/**
 * Get default headers including organization context for multi-tenant security
 */
const getDefaultHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const organizationId = getOrganizationId();
  if (organizationId) {
    headers['X-Organization-Id'] = organizationId;
  }

  return headers;
};

// ============================================================================
// Dynamic ERP Client Management
// ============================================================================

/**
 * Cache for ERP client instances keyed by base URL
 * This allows us to reuse clients for the same URL
 */
const erpClientCache = new Map<string, ERPServiceClient>();

/**
 * Current active ERP base URL
 * Starts empty - must be set by ERPConfigContext after fetching FPO config
 */
let currentErpBaseUrl = '';
console.log('[API Client] Initial ERP base URL: (empty - waiting for FPO config)');

/**
 * Create an ERP client for a specific base URL
 */
const createERPClient = (baseURL: string): ERPServiceClient => {
  const normalizedUrl = baseURL.replace(/\/$/, ''); // Remove trailing slash

  return createERPService({
    baseURL: normalizedUrl,
    getAccessToken,
    timeout: config.apiTimeout,
    defaultHeaders: getDefaultHeaders(),
    // Note: Do NOT set Content-Type here. The apiClient sets it automatically:
    // - 'application/json' for JSON requests
    // - 'multipart/form-data' with boundary for FormData (file uploads)
  });
};

/**
 * Get or create an ERP client for a specific base URL
 * Uses caching to avoid recreating clients for the same URL
 */
export const getERPClient = (baseURL?: string): ERPServiceClient => {
  const url = (baseURL || currentErpBaseUrl).replace(/\/$/, '');

  let client = erpClientCache.get(url);
  if (!client) {
    console.log('[API Client] Creating new ERP client for URL:', url);
    client = createERPClient(url);
    erpClientCache.set(url, client);
  }

  return client;
};

/**
 * Set the current ERP base URL (called by ERPConfigContext when config is loaded)
 * This updates the default URL used by erpClient
 */
export const setERPBaseUrl = (baseURL: string): void => {
  const normalizedUrl = baseURL.replace(/\/$/, '');
  if (normalizedUrl !== currentErpBaseUrl) {
    console.log('[API Client] Updating ERP base URL:', normalizedUrl);
    currentErpBaseUrl = normalizedUrl;
  }
};

/**
 * Get the current ERP base URL
 */
export const getERPBaseUrl = (): string => currentErpBaseUrl;

/**
 * Reset ERP client state (call on logout or user switch)
 * Clears the cached clients and resets the base URL
 */
export const resetERPClient = (): void => {
  console.log('[API Client] Resetting ERP client state');
  currentErpBaseUrl = '';
  erpClientCache.clear();
};

/**
 * Dynamic ERP Service Client getter
 *
 * This uses a Proxy to ensure that all property accesses on erpClient
 * are forwarded to the client with the CURRENT base URL (not the URL at module load time).
 *
 * This allows code that imports `erpClient` directly to automatically
 * use the dynamic URL set by ERPConfigContext without any code changes.
 */
const createDynamicERPClientProxy = (): ERPServiceClient => {
  return new Proxy({} as ERPServiceClient, {
    get(_target, prop) {
      // Check if ERP URL is configured
      if (!currentErpBaseUrl) {
        console.warn('[ERP Proxy] No ERP base URL configured. Waiting for FPO config to load.');
        // Return a no-op function that returns a rejected promise for methods
        if (typeof prop === 'string' && !['then', 'catch', 'finally'].includes(prop)) {
          return new Proxy({}, {
            get() {
              return () => Promise.reject(new Error('ERP service not configured. Please wait for FPO config to load.'));
            }
          });
        }
        return undefined;
      }

      // Always get the client with the current URL
      console.log('[ERP Proxy] Accessing property:', String(prop), 'using URL:', currentErpBaseUrl);
      const client = getERPClient(currentErpBaseUrl);
      const value = (client as any)[prop];

      // If it's a function, bind it to the client
      if (typeof value === 'function') {
        return value.bind(client);
      }

      return value;
    },
  });
};

/**
 * Default ERP Service Client Instance
 *
 * This is a dynamic proxy that ALWAYS uses the current ERP base URL.
 * When ERPConfigContext calls setERPBaseUrl(), subsequent calls through
 * this erpClient will automatically use the new URL.
 *
 * This maintains backwards compatibility with existing code that imports
 * `erpClient` directly while supporting multi-tenant deployments.
 */
export const erpClient: ERPServiceClient = createDynamicERPClientProxy();

// ============================================================================
// Auth Client (Static - single deployment)
// ============================================================================

/**
 * Auth Service Client Instance
 * Configured with authentication from Redux store
 */
export const authClient = createAAAService({
  baseURL: config.authServiceUrl,
  getAccessToken,
  defaultHeaders: getDefaultHeaders(),
});

/**
 * Type exports for use throughout the application
 */
export type { ERPServiceClient };
export type AuthServiceClient = ReturnType<typeof createAAAService>;
